import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

// Function to generate CSV content from trip data with all table fields
const generateCsv = (trip, stopsWithCumulativeKm, startKilometer) => {
  const headers = [
    'Stop Number',
    'Location',
    'Type',
    'Client Type',
    'Client', 
    'City',
    'Address',
    'ZIP',
    'Comment',
    'Distance (km)',
    'Cumulative KM',
    'Latitude',
    'Longitude'
  ].join(',') + '\n';
  
  // Use the enhanced data with cumulative kilometers
  const rows = stopsWithCumulativeKm.map((stop, index) => {
    // Escape any commas in text fields
    const escapeCsv = (text) => {
      if (text === undefined || text === null) return '';
      text = String(text);
      const needsQuotes = text.includes(',') || text.includes('"') || text.includes('\n');
      if (!needsQuotes) return text;
      return `"${text.replace(/"/g, '""')}"`;
    };
    
    return [
      index + 1, // Stop Number
      escapeCsv(stop.name),
      stop.type,
      escapeCsv(stop.client_type || ''),
      escapeCsv(stop.client || ''),
      escapeCsv(stop.city || ''),
      escapeCsv(stop.address || ''),
      stop.zip || '',
      escapeCsv(stop.comment || ''),
      stop.distance_from_previous || '0',
      stop.cumulative_adjusted_km.toFixed(1),
      stop.latitude,
      stop.longitude
    ].join(',');
  }).join('\n');
  
  // Add trip metadata as a header comment
  const metadata = [
    '# Trip Export',
    `# Date: ${new Date().toLocaleString()}`,
    `# Start Location: ${trip.trip[0]?.name || 'Unknown'}`,
    `# End Location: ${trip.trip[trip.trip.length-1]?.name || 'Unknown'}`,
    `# Total Stops: ${trip.trip.length}`,
    `# Start Kilometer: ${startKilometer !== undefined ? startKilometer.toFixed(1) : 'N/A'}`,
    `# Total Distance: ${stopsWithCumulativeKm[stopsWithCumulativeKm.length-1]?.cumulative_adjusted_km.toFixed(1) || 'Unknown'} km`,
    ''
  ].join('\n');
  
  return metadata + headers + rows;
};

// Function to export trip as CSV
const exportToCsv = (trip, stopsWithCumulativeKm, startKilometer) => {
  const csvContent = generateCsv(trip, stopsWithCumulativeKm, startKilometer);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Generate a descriptive filename including origin and destination
  const origin = trip.trip[0]?.name?.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 15) || 'Start';
  const destination = trip.trip[trip.trip.length-1]?.name?.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 15) || 'End';
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const filename = `trip_${origin}-${destination}_${today}.csv`;
  
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Function to get color for stop type
const getStopTypeColor = (type) => {
  switch(type) {
    case 'start':
      return 'primary';
    case 'intermediate':
      return 'warning';
    case 'end':
      return 'success';
    default:
      return 'default';
  }
};

const TripDetails = ({ trip, scores, startKilometer, endKilometer }) => {
  // Access API usage data if available
  const apiUsage = trip?.api_usage;
  // Calculate total distance
  const totalDistance = trip.total_kilometers || trip.trip.reduce(
    (total, stop) => total + (stop.distance_from_previous || 0),
    0
  );
  
  // Calculate cumulative kilometers for each stop
  const stopsWithCumulativeKm = trip.trip.map((stop, index) => {
    let cumulativeKm = startKilometer || 0;
    
    // Calculate cumulative values by summing up all previous stops
    for (let i = 0; i <= index; i++) {
      if (i > 0) { // Skip first stop (it's the starting point)
        const currentStop = trip.trip[i];
        cumulativeKm += (currentStop.distance_from_previous || 0);
      }
    }
    
    return {
      ...stop,
      cumulative_adjusted_km: cumulativeKm
    };
  });
  
  // Check if final kilometer matches the expected end kilometer with 5% tolerance
  const finalStopIndex = stopsWithCumulativeKm.length - 1;
  const finalKm = stopsWithCumulativeKm[finalStopIndex].cumulative_adjusted_km;
  const finalKmDifference = endKilometer ? Math.abs(finalKm - endKilometer) : 0;
  const finalKmDifferencePercent = endKilometer ? (finalKmDifference / endKilometer) * 100 : 0;
  const finalKmMatch = endKilometer ? finalKmDifferencePercent <= 5 : true;
  
  return (
    <Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs>
          <Typography variant="h6" gutterBottom>
            Trip Details
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<SaveAltIcon />}
            onClick={() => exportToCsv(trip, stopsWithCumulativeKm, startKilometer)}
            title="Export all trip data with coordinates and cumulative kilometers"
          >
            Export as CSV
          </Button>
        </Grid>
      </Grid>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1">
          <strong>Total Distance:</strong> {totalDistance.toFixed(1)} km
        </Typography>
        <Typography variant="body1">
          <strong>Number of Stops:</strong> {trip.trip.length}
        </Typography>
        
        {/* Add combinations tried information */}
        {(trip.combinations_tried || (trip.api_usage && trip.api_usage.calls)) && (
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            <strong>Combinations Analyzed:</strong> {(trip.combinations_tried || (trip.api_usage ? trip.api_usage.calls + trip.api_usage.cache_hits : 0)).toLocaleString()}
          </Typography>
        )}
        
        {/* Add kilometer verification information */}
        {startKilometer !== undefined && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f8f8', borderRadius: 2 }}>
            <Typography variant="body1">
              <strong>Start Kilometer:</strong> {startKilometer.toFixed(1)}
            </Typography>
            <Typography variant="body1">
              <strong>Final Calculated Kilometer:</strong> {finalKm.toFixed(1)}
            </Typography>
            {endKilometer !== undefined && (
              <Typography variant="body1">
                <strong>Expected End Kilometer:</strong> {endKilometer.toFixed(1)}
                <Chip 
                  label={finalKmMatch ? "Within 5% - OK" : `Difference: ${finalKmDifference.toFixed(1)} km (${finalKmDifferencePercent.toFixed(1)}%)`} 
                  size="small" 
                  color={finalKmMatch ? "success" : "error"}
                  sx={{ marginLeft: 1, height: 20 }}
                />
              </Typography>
            )}
            
            {/* Add rental/trip distance match accuracy */}
            {trip.rental_kilometers && (
              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eaeaea' }}>
                <Typography variant="body2">
                  <strong>Rental Distance:</strong> {trip.rental_kilometers.toFixed(1)} km
                </Typography>
                <Typography variant="body2">
                  <strong>Generated Trip Distance:</strong> {trip.total_kilometers.toFixed(1)} km
                </Typography>
                <Typography variant="body2">
                  <strong>Distance Accuracy:</strong>
                  {trip.error_percent !== undefined ? (
                    <Chip 
                      label={trip.error_percent <= 1 ? "Excellent Match (<1%)" : 
                             trip.error_percent <= 2 ? "Very Good Match (<2%)" :
                             trip.error_percent <= 5 ? "Good Match (<5%)" : "Fair Match"}
                      size="small" 
                      color={trip.error_percent <= 1 ? "success" : 
                             trip.error_percent <= 2 ? "success" : 
                             trip.error_percent <= 5 ? "warning" : "error"}
                      sx={{ marginLeft: 1, height: 20 }}
                    />
                  ) : (
                    <Chip 
                      label={Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 1 ? "Excellent Match (<1%)" : 
                             Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 2 ? "Very Good Match (<2%)" :
                             Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 5 ? "Good Match (<5%)" : "Fair Match"}
                      size="small" 
                      color={Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 1 ? "success" : 
                             Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 2 ? "success" : 
                             Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100 <= 5 ? "warning" : "error"}
                      sx={{ marginLeft: 1, height: 20 }}
                    />
                  )}
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                    Difference: {trip.kilometer_diff ? 
                      trip.kilometer_diff.toFixed(1) : 
                      Math.abs(trip.total_kilometers - trip.rental_kilometers).toFixed(1)} km 
                    ({trip.error_percent ? 
                      trip.error_percent.toFixed(2) : 
                      (Math.abs(trip.total_kilometers - trip.rental_kilometers) / trip.rental_kilometers * 100).toFixed(2)}%)
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Client Type</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>ZIP</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell align="right">Distance (km)</TableCell>
              <TableCell align="right">Cumulative KM</TableCell>
              <TableCell align="right">Coordinates</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stopsWithCumulativeKm.map((stop, index) => (
              <TableRow key={index} sx={{ userSelect: 'text' }}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'text' }}>
                    <LocationOnIcon 
                      color={
                        stop.type === 'start' ? 'primary' :
                        stop.type === 'end' ? 'secondary' : 'action'
                      } 
                      sx={{ mr: 1 }} 
                    />
                    {stop.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={stop.type.charAt(0).toUpperCase() + stop.type.slice(1)} 
                    color={getStopTypeColor(stop.type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={stop.client_type ? 'medium' : 'normal'}>
                    {stop.client_type || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={stop.client ? 'medium' : 'normal'} color={stop.client ? 'primary.main' : 'text.secondary'}>
                    {stop.client || '-'}
                  </Typography>
                </TableCell>
                <TableCell>{stop.city || '-'}</TableCell>
                <TableCell>{stop.address || '-'}</TableCell>
                <TableCell>{stop.zip || '-'}</TableCell>
                <TableCell>{stop.comment || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Distance from previous stop">
                    <span>{stop.distance_from_previous ? `${stop.distance_from_previous} km` : '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Tooltip title="Total kilometers from the start point">
                      <Typography variant="body2">{stop.cumulative_adjusted_km.toFixed(1)}</Typography>
                    </Tooltip>
                    {index === finalStopIndex && endKilometer !== undefined && (
                      <Tooltip title={finalKmMatch ? "Final kilometer value matches the rental record" : "Final kilometer value does not match the rental record"}>
                        <Chip 
                          size="small" 
                          variant="outlined"
                          color={finalKmMatch ? "success" : "error"}
                          label={finalKmMatch ? "✓" : "✗"} 
                          sx={{ ml: 1, height: 16, minWidth: '16px', fontSize: '0.6rem' }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ userSelect: 'text' }}>
                  {`${stop.latitude.toFixed(4)}, ${stop.longitude.toFixed(4)}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Simple Trip Visualization */}
      <Box sx={{ my: 3, py: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Trip Route
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {stopsWithCumulativeKm.map((stop, index) => (
            <React.Fragment key={index}>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: 1,
                  bgcolor: 
                    stop.type === 'start' ? 'primary.light' :
                    stop.type === 'end' ? 'secondary.light' : 'warning.light',
                  width: '80%',
                  maxWidth: '500px',
                  mb: index === stopsWithCumulativeKm.length - 1 ? 0 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  userSelect: 'text'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    <strong>{stop.name}</strong> ({stop.type})
                  </Typography>
                  
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight="medium">
                      <Tooltip title="Total kilometers from the start point">
                        <span>KM: {stop.cumulative_adjusted_km.toFixed(1)}</span>
                      </Tooltip>
                      {index === finalStopIndex && endKilometer !== undefined && (
                        <Tooltip title={finalKmMatch ? "Final kilometer value matches the rental record" : `Final kilometer value differs by ${finalKmDifferencePercent.toFixed(1)}% from the rental record`}>
                          <Chip 
                            size="small" 
                            variant="outlined"
                            color={finalKmMatch ? "success" : "error"}
                            label={finalKmMatch ? "✓" : `${finalKmDifferencePercent.toFixed(1)}%`} 
                            sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  {stop.distance_from_previous && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      <Tooltip title="Distance from previous stop">
                        <span>Segment: {stop.distance_from_previous} km</span>
                      </Tooltip>
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ mt: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                  {stop.client && (
                    <Typography 
                      variant="caption" 
                      display="block" 
                      sx={{ 
                        bgcolor: 'primary.50',
                        py: 0.5,
                        px: 1,
                        borderRadius: 0.5,
                        fontWeight: 'medium'
                      }}
                    >
                      <strong>Client:</strong> {stop.client} {stop.client_type && `(${stop.client_type})`}
                    </Typography>
                  )}
                  {stop.address && (
                    <Typography variant="caption" display="block">
                      <strong>Address:</strong> {stop.address}, {stop.zip || ''} {stop.city || ''}
                    </Typography>
                  )}
                  {stop.comment && (
                    <Typography variant="caption" display="block">
                      <strong>Note:</strong> {stop.comment}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              {index < trip.trip.length - 1 && (
                <ArrowDownwardIcon color="action" fontSize="small" />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TripDetails;